<template>
  <div class="pool-select-nav-wrapper" :class="{ 'sticky': sticky }">
    <div class="pool-select-nav-upper">
      <div class="content-container-column upper-menu-wrapper">
        <ul class="category-list">
          <li
            v-for="category in poolData"
            v-bind:key="category.tag"
            role="button"
            tabindex="0"
            v-on:click="handleCategorySelected(category)"
            v-on:keyup.enter="handleCategorySelected(category)"
            v-on:keyup.space="handleCategorySelected(category)"
          >
            <div 
              class="category-select-btn" 
              :class="{ active: $store.state.selectedCategory === category }"
            >
              {{ category.title }}
            </div>
          </li>
        </ul>
      </div>
    </div>
    <div class="pool-select-nav-lower content-container-column">
      <div class="edge-eclipse">
        <div class="scroll-container">
          <ul class="series-list">
            <li
              v-for="series in $store.state.selectedCategory.series"
              v-bind:key="series.tag">
              <div 
                class="series-select-btn" 
                :class="{ active: $store.state.selectedSeries === series }" 
                role="button"
                tabindex="0"
                v-on:click="handleSeriesSelected(series)"
                v-on:keyup.enter="handleSeriesSelected(series)"
                v-on:keyup.space="handleSeriesSelected(series)"
              >
                {{ series.title }}
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="series-description content-container-column-narrower">
      <p v-if="$store.state.selectedSeries.description">
        {{ $store.state.selectedSeries.description }}
      </p>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';

export default {
  name: 'PoolSelectNav',
  props: {
    sticky: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isDown: false
    }
  },
  computed: {
    ...mapGetters([
      'poolData',
      'selectedCategory'
    ]),
  },
  methods: {
    handleCategorySelected(category) {
      // Commit the category change to the store
      this.$store.commit('setCategory', category);
      
      // track event for Google Analytics
      this.$gtag.event(category.title + ' clicked', {
        'event_category': 'Pool Collection Pick',
        'event_label': category.title + ' clicked'
      });
    },
    handleSeriesSelected(series) {
      this.$store.commit('setSeries', series);
      // track event for Google Analytics
      this.$gtag.event(series.title + ' clicked', {
        'event_category': 'Pool Collection Pick',
        'event_label': series.title + ' clicked'
      });
    },
    scrollToSelectedSeries() {
      this.$nextTick(() => {
        const activeSeriesElement = this.$el.querySelector('.series-select-btn.active');
        if (activeSeriesElement) {
          const scrollContainer = this.$el.querySelector('.scroll-container');
          const containerWidth = scrollContainer.offsetWidth;
          const elementLeft = activeSeriesElement.offsetLeft;
          const elementWidth = activeSeriesElement.offsetWidth;
          
          // Center the element in the container with smooth scrolling
          scrollContainer.scrollTo({
            left: elementLeft - (containerWidth / 2) + (elementWidth / 2),
            behavior: 'smooth'
          });
        }
      });
    },
    setUpDesktopHorizontalScroll() {
      /* 
          The series menu is a horizontally scrolling menu without a scrollbar
          (if the content is wider than the element). 
          So, we need a way for the user to scroll horizontally on desktop
          in a way that is more intuitive than using the mouse wheel and shift key.
      */
      const el = document.querySelector('.scroll-container');
      let startX;
      let scrollLeft;
      el.addEventListener('mousedown', (e) => {
        this.isDown = true;
        startX = e.pageX - el.offsetLeft;
        scrollLeft = el.scrollLeft;
      });
      el.addEventListener('mouseleave', () => {
        this.isDown = false;
      });
      el.addEventListener('mouseup', () => {
        this.isDown = false;
      });
      el.addEventListener('mousemove', (e) => {
        if (!this.isDown) return;
        const x = e.pageX - el.offsetLeft;
        const walk = (x - startX);
        el.scrollLeft = scrollLeft - walk;
      });
    }
  },
  mounted() {
    this.setUpDesktopHorizontalScroll();
  }
};
</script>
<style scoped lang="scss">
  .pool-select-nav-wrapper {
    background-color: $navy;
    
    &.sticky {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      @media #{ $desktop } {
        right: $scrollbar-width;
      }
    }
  }
  .pool-select-nav-upper {
    background-color: $deep-navy;
  }
  .upper-menu-wrapper {
    text-align: center; // Center the UL by centering its container
  }
  ul {
    display: inline-block;
    padding-inline-start: 0;
  }
  ul.category-list {
    list-style: none;
    margin: 0;
    li {
      display: inline;
      padding: 0;
      .category-select-btn {
        display:inline-block;
        padding: 21px 24px;
        margin: 0;
        background-color: transparent;
        &.active {
          background-color: $navy;
        }
      }
    }
  }
  .pool-select-nav-lower {
    background-color: $navy;
    width: 100%;
    .edge-eclipse {
      position: relative; // Added for pseudo-element positioning
      // Add edge gradients
      &::before,
      &::after {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        width: 50px;
        pointer-events: none;
      }
      &::before {
        left: 0;
        background: linear-gradient(to right, $navy, transparent);
      }
      &::after {
        right: 0;
        background: linear-gradient(to left, $navy, transparent);
      }
      .scroll-container {
        display: flex;
        justify-content: center;
        overflow-x: auto;
        -ms-overflow-style: none;
        scrollbar-width: none;
        &::-webkit-scrollbar {
          display: none;
        }
      }
    }
  }
  ul.series-list {
    width: auto;
    max-width: 100%;
    list-style: none;
    margin: 0 auto;
    display: inline-flex;
    white-space: nowrap;
    flex-wrap: nowrap;
    li {
      display: inline-block;
      padding: 0;
      flex-shrink: 0;
      &:first-child {
        .series-select-btn {
          margin-left: 25px;
        }
      }
      &:last-child {
        .series-select-btn {
          margin-right: 25px;
        }
      }
      .series-select-btn {
        margin: 19px 28px;
      }
    }
  }
  ul li .category-select-btn, ul li .series-select-btn {
    font-size:1rem;
    font-family: 'Europa Regular';
    line-height: 1rem;
    color: $aqua;
    transition: color 200ms ease-out, font-weight 200ms ease-out;
    user-select: none;
    cursor: pointer;
    &.active {
      color: $white;
      font-family: 'Europa Bold';
    }
    &:focus:not(:focus-visible) {
      outline: none;
    }
    &:focus {
      outline: 2px solid $aqua;
      outline-offset: -2px;
    }
  }
  .series-description {
    text-align: center;
    padding: 0 4% 16px;
    user-select: none;
    p {
      font-family: 'Europa Light';
    }
  }
</style>
